import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getPublicInvitation, trackPublicInvitationView, ApiError } from '@/src/infrastructure/lib/api';
import type { ApiInvitation } from '@/src/types';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { Button } from '@/src/modules/components/Button';
import { CinematicWeddingExperience } from '@/src/modules/experiences/wedding/CinematicWeddingExperience';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { PartnerAttributionFooter } from '@/src/modules/components/PartnerAttributionFooter';
import { Watermark } from '@/src/modules/components/Watermark';
import { Helmet } from 'react-helmet-async';
import { ShareInvitationModal } from '@/src/modules/invitations/components/ShareInvitationModal';
import { SceneExperienceRenderer } from '@/src/modules/experiences/scene/SceneExperienceRenderer';
import { ExperienceProgress } from '@/src/modules/experiences/scene/ExperienceProgress';
import { Icons } from '@/src/modules/components/Icons';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { trackEvent } from '@/src/infrastructure/lib/analytics';
import { startTemplateView } from '@/src/infrastructure/lib/template-analytics';
import { safeFormatDateLong, safeFormatTime } from '@/src/infrastructure/lib/weddingDataHelper';


type PageState =
  | { status: 'loading' }
  | { status: 'ready'; invitation: ApiInvitation }
  | { status: 'expired' }
  | { status: 'deleted' }
  | { status: 'missing'; message: string };

function resolveAbsoluteUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

function ExpiredPage() {
  const { user } = useAuth();
  const destination = user ? '/dashboard' : '/login';
  return (
    <div className="min-h-screen bg-gradient-to-br from-surface to-surface-low flex items-center justify-center p-6">
      <div className="glass-panel max-w-md text-center p-8 rounded-2xl shadow-xl border border-outline-variant/20">
        <div className="text-6xl mb-6 animate-float">✨</div>
        <h1 className="text-3xl font-bold text-brand-deep mb-4">Event Concluded</h1>
        <p className="text-on-surface-variant text-lg mb-3">
          The event has occurred, and thus this website has been taken down and is now offline.
        </p>
        <p className="text-on-surface-variant/70 text-sm mb-8">
          Thank you for visiting! You can create your own beautiful digital event experiences with InviteCloud.
        </p>
        <Link to={destination}>
          <Button className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl w-full shadow-sm">
            Visit InviteCloud
          </Button>
        </Link>
      </div>
    </div>
  );
}

function DeletedPage() {
  return (
    <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="text-6xl mb-6">🔒</div>
        <h1 className="text-3xl font-bold text-on-surface mb-3">Invitation Unavailable</h1>
        <p className="text-on-surface-variant mb-6">
          This invitation link is not publicly active. If you created this draft, please log in to publish it.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/wizard">
            <Button variant="primary">Create or Edit Invitation</Button>
          </Link>
          <Link to="/">
            <Button variant="outline">Go to InviteCloud</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function findLastVisibleSection(): HTMLElement | null {
  const container = document.querySelector('[data-testid="public-invitation-container"]')
    || document.querySelector('article')
    || document.querySelector('main')
    || document.body;

  const elements = Array.from(container.querySelectorAll('section, [data-section-type], div.relative.z-10, article > div, main > div'));
  
  const visible = elements.filter((el) => {
    const html = el as HTMLElement;
    if (!html) return false;

    const rect = html.getBoundingClientRect();
    if (rect.height <= 0 || rect.width <= 0) return false;
    if (window.getComputedStyle(html).display === 'none') return false;

    const id = html.id || '';
    const className = html.className || '';
    const sectionType = html.getAttribute('data-section-type') || '';
    
    if (
      id === 'partner-attribution-portal-root' ||
      sectionType === 'partner-attribution' ||
      html.tagName === 'FOOTER' ||
      html.tagName === 'NAV' ||
      html.tagName === 'HEADER' ||
      html.getAttribute('data-no-print') !== null ||
      html.getAttribute('data-watermark') !== null ||
      html.querySelector('[data-watermark]') !== null ||
      html.querySelector('a[href*="invitecloud.in"]') !== null
    ) {
      return false;
    }

    return true;
  });

  if (visible.length === 0) return null;

  return visible[visible.length - 1] as HTMLElement;
}

export function PublicInvitation() {
  const { slug } = useParams();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [searchParams] = useSearchParams();
  const isPrintMode = searchParams.get('print') === 'true';
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [showShareModal, setShowShareModal] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const printTriggered = useRef(false);

  useEffect(() => {
    if (!slug) {
      setState({ status: 'missing', message: 'Invitation link is missing.' });
      return;
    }

    getPublicInvitation(slug)
      .then((invitation) => {
        setState({ status: 'ready', invitation });
        // Shared template analytics (page_view + guest interactions) — template-analytics.ts.
        // First, so every later hit on this page (incl. GUEST_INVITATION_VIEW) carries the redacted
        // page_location/page_title instead of the slug and the invitation title.
        startTemplateView(window.location.pathname, {
          surface: 'invitation',
          templateId: invitation.templateId,
          occasionType: invitation.occasionType,
          invitationId: invitation.id,
        });
        trackEvent('GUEST_INVITATION_VIEW', 'public_view', slug);
        try {
          localStorage.setItem(
            'invitecloud.attribution.v1',
            JSON.stringify({
              referredByInvitationSlug: slug,
              timestamp: new Date().toISOString(),
            }),
          );
        } catch (_) {}
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          if (err.status === 410) {
            startTemplateView(window.location.pathname, { surface: 'invitation' }, 'expired');
            setState({ status: 'expired' });
            return;
          }
          if (err.status === 404) {
            startTemplateView(window.location.pathname, { surface: 'invitation' }, 'deleted');
            setState({ status: 'deleted' });
            return;
          }
        }
        startTemplateView(window.location.pathname, { surface: 'invitation' }, 'missing');
        setState({
          status: 'missing',
          message: err instanceof Error ? err.message : 'Invitation not found.',
        });
      });
  }, [slug]);

  // Print mode: once the invitation is ready, trigger window.print() then close.
  useEffect(() => {
    if (isPrintMode && state.status === 'ready' && !printTriggered.current) {
      printTriggered.current = true;
      // Give the template time to paint fully before printing.
      const timer = setTimeout(() => {
        window.print();
        window.close();
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isPrintMode, state.status]);

  useEffect(() => {
    if (state.status !== 'ready') {
      return;
    }
    const invitation = state.invitation;
    if (!invitation.partnerAttribution?.enabled) {
      return;
    }

    function reposition() {
      const lastSection = findLastVisibleSection();
      if (lastSection) {
        let target = document.getElementById('partner-attribution-portal-root');
        if (!target) {
          target = document.createElement('div');
          target.id = 'partner-attribution-portal-root';
          target.className = 'w-full';
        }
        if (lastSection.nextSibling !== target) {
          lastSection.after(target);
        }
        if (portalTarget !== target) {
          setPortalTarget(target);
        }
      }
    }

    // Run repositioning after paint
    const timer = setTimeout(reposition, 100);

    const observer = new MutationObserver(() => {
      reposition();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      const target = document.getElementById('partner-attribution-portal-root');
      if (target && target.parentNode) {
        target.parentNode.removeChild(target);
      }
    };
  }, [state.status]);

  // Analytics tracking
  useEffect(() => {
    if (state.status === 'ready' && !isPrintMode) {
      const viewerIdKey = 'invitecloud_anon_viewer_id';
      let anonViewerId: string | null = null;
      try {
        anonViewerId = typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(viewerIdKey) : null;
      } catch (_) {}
      if (!anonViewerId) {
        anonViewerId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `anon-${Date.now()}`;
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(viewerIdKey, anonViewerId);
          }
        } catch (_) {}
      }
      trackPublicInvitationView(state.invitation.id, anonViewerId).catch(console.error);
    }
  }, [state.status, isPrintMode]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (state.status === 'expired') return <ExpiredPage />;
  if (state.status === 'deleted') return <DeletedPage />;

  if (state.status === 'missing') {
    return (
      <div className="min-h-screen bg-surface-lowest flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-bold text-brand-deep mb-3">Invitation unavailable</h1>
          <p className="text-on-surface-variant mb-6">{state.message}</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { invitation } = state;
  const template = invitation.template as ApiInvitation['template'] | undefined;
  const attribution = invitation.partnerAttribution ?? null;

  // Common Helmet Metadata + Share Button to be included in all views.
  // In print mode, sharing UI and nav are hidden via CSS @media print.
  const renderSharedUI = () => {
    const rawImage = invitation.mediaUrls?.[0] || invitation.template?.previewImageUrl || null;
    const shareImage = resolveAbsoluteUrl(rawImage);
    const isOwner = !!(user && user.id === invitation.userId);

    return (
      <>
        <Helmet>
          <title>{invitation.eventName}</title>
          <meta name="description" content={invitation.customMessage || `Hosted by ${invitation.hostName}`} />
          <meta property="og:title" content={invitation.eventName} />
          <meta property="og:description" content={invitation.customMessage || `Hosted by ${invitation.hostName}`} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={invitation.hostedUrl ?? window.location.href} />
          {shareImage && (
            <>
              <meta property="og:image" content={shareImage} />
              <meta property="og:image:width" content="1200" />
              <meta property="og:image:height" content="630" />
              <meta name="twitter:image" content={shareImage} />
            </>
          )}
          <meta name="twitter:card" content={shareImage ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={invitation.eventName} />
          <meta name="twitter:description" content={invitation.customMessage || `Hosted by ${invitation.hostName}`} />
          {isPrintMode && (
            <style>{`
              @media print {
                /* Hide interactive chrome */
                [data-no-print], nav, header, footer,
                button, [role="button"] { display: none !important; }

                /* Allow content to flow beyond viewport */
                html, body {
                  height: auto !important;
                  overflow: visible !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }

                /* Override every viewport-height constraint so full content prints */
                *,
                [class*="h-screen"],
                [class*="min-h-screen"],
                [class*="max-h-screen"],
                [class*="overflow-hidden"],
                [class*="overflow-y-hidden"],
                [class*="overflow-x-hidden"] {
                  overflow: visible !important;
                  height: auto !important;
                  max-height: none !important;
                  min-height: 0 !important;
                }

                /*
                 * Reset Framer Motion animations: sections with whileInView
                 * stay at initial state (opacity:0, translate) until scrolled
                 * into view — in print mode nothing is ever "in view", so
                 * every section would be invisible without this reset.
                 */
                * {
                  opacity: 1 !important;
                  transform: none !important;
                  animation: none !important;
                  transition: none !important;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }

                /* Remove fixed-position floating buttons from print layout */
                .fixed { position: static !important; }

                /* Remove page margins set by the printer */
                @page {
                  margin: 0;
                  size: auto;
                }
              }
            `}</style>
          )}
        </Helmet>

        {/* Floating host controls — Edit + Share — hidden in print mode */}
        {isOwner && !isPrintMode && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3" data-no-print>
            <Button
              onClick={() => navigate(`/wizard?draftId=${encodeURIComponent(invitation.id)}`)}
              variant="outline"
              className="shadow-xl rounded-full h-14 px-6 flex items-center gap-2 bg-surface"
            >
              <Icons.Edit3 size={20} />
              <span className="font-bold">Edit</span>
            </Button>
            <Button
              onClick={() => setShowShareModal(true)}
              className="shadow-xl rounded-full h-14 px-6 flex items-center gap-2"
            >
              <Icons.Share size={20} />
              <span className="font-bold">Share</span>
            </Button>
          </div>
        )}

        {isOwner && showShareModal && (
          <ShareInvitationModal
            invitation={invitation}
            onClose={() => setShowShareModal(false)}
          />
        )}
      </>
    );
  };

  const templateMetadata = registry.get(invitation.templateId)?.metadata;
  const themeBgColor = templateMetadata?.colorScheme?.bg || (template as any)?.themeConfig?.backgroundColor || null;
  const effectiveBgColor = themeBgColor || (invitation.occasionType === 'WEDDING' && !registry.has(invitation.templateId) ? '#11130f' : undefined);

  const renderFallbackCard = () => {
    const formattedDate = safeFormatDateLong(invitation?.eventDate, 'en-IN', 'Date to be announced');
    const formattedTime = safeFormatTime(invitation?.eventDate, 'en-IN', '');
    const mapsUrl = invitation?.eventVenue
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(invitation.eventVenue)}`
      : null;

    return (
      <div className="min-h-screen bg-surface-lowest py-16 px-4 flex items-center justify-center">
        <article className="max-w-xl w-full mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-outline-variant/30">
          <div className="h-3 bg-gradient-to-r from-amber-500 via-rose-500 to-amber-600" />
          <div className="p-8 sm:p-10 text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-50 border border-rose-200/60 mb-4">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700">
                {(invitation?.occasionType || 'EVENT').replace(/_/g, ' ')}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mt-2">
              {invitation?.eventName || 'Special Invitation'}
            </h1>

            {invitation?.hostName && (
              <p className="text-stone-600 font-medium mt-2">
                Hosted with joy by <span className="text-stone-900 font-semibold">{invitation.hostName}</span>
              </p>
            )}

            <div className="my-8 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />

            <div className="space-y-4 text-left bg-stone-50/80 rounded-2xl p-5 border border-stone-200/50">
              <div className="flex items-start gap-3.5">
                <span className="text-xl">📅</span>
                <div>
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Date & Time</p>
                  <p className="text-stone-900 font-medium">
                    {formattedDate} {formattedTime ? `· ${formattedTime}` : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3.5">
                <span className="text-xl">📍</span>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Venue</p>
                  <p className="text-stone-900 font-medium">
                    {invitation?.eventVenue || 'Venue details to be announced'}
                  </p>
                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold text-rose-600 hover:text-rose-700 underline"
                    >
                      <span>Open in Google Maps</span>
                      <span>↗</span>
                    </a>
                  )}
                </div>
              </div>
            </div>

            {invitation?.customMessage && (
              <div className="mt-6 p-4 rounded-xl bg-amber-50/60 border border-amber-200/50 italic text-stone-700 text-sm">
                "{invitation.customMessage}"
              </div>
            )}
          </div>
        </article>
      </div>
    );
  };

  let content: React.ReactNode;

  if (registry.has(invitation.templateId)) {
    content = (
      <TemplateRenderer
        templateId={invitation.templateId}
        data={invitation}
        hasPartnerAttribution={!!attribution}
        themeBgColor={themeBgColor}
      />
    );
  } else if (invitation.occasionType === 'WEDDING' && template?.renderMode !== 'experience') {
    content = (
      <CinematicWeddingExperience
        data={invitation}
        hasPartnerAttribution={!!attribution}
      />
    );
  } else if (template?.renderMode === 'experience' && !(invitation as any).sceneManifest) {
    // The Experience DSL hasn't compiled into a SceneManifest yet — show a
    // building-your-experience progress screen. Early return, bypassing the shared
    // wrapper/error-boundary/attribution below: there is nothing to attribute a
    // partner to yet while the experience itself doesn't exist.
    return (
      <div data-testid="public-invitation-container">
        {renderSharedUI()}
        <ExperienceProgress invitationSlug={invitation.publicSlug!} coupleNames={invitation.hostName || undefined} />
      </div>
    );
  } else if (template?.renderMode === 'experience') {
    content = <SceneExperienceRenderer manifest={(invitation as any).sceneManifest} />;
  } else if (template?.renderMode === 'json-renderer' && template.layoutSchema) {
    content = (
      <JsonTemplateRenderer
        schema={template.layoutSchema}
        theme={template.themeConfig}
        data={invitation}
        mode="live"
        hasPartnerAttribution={!!attribution}
      />
    );
  } else {
    content = renderFallbackCard();
  }

  return (
    <div
      data-testid="public-invitation-container"
      style={effectiveBgColor ? { backgroundColor: effectiveBgColor } : undefined}
    >
      {renderSharedUI()}
      <PublicInvitationErrorBoundary fallback={renderFallbackCard()}>
        {content}
      </PublicInvitationErrorBoundary>
      {attribution && (
        portalTarget
          ? createPortal(<PartnerAttributionFooter attribution={attribution} themeBgColor={effectiveBgColor || null} />, portalTarget)
          : <PartnerAttributionFooter attribution={attribution} themeBgColor={effectiveBgColor || null} />
      )}
    </div>
  );
}

export class PublicInvitationErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[PublicInvitation] Live template rendering failed:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

